import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  Box,
  Typography,
  TablePagination,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import storageService from '../../../../Utils/storageService';
import { getFirstLetters } from '../../Helpers/helper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const useStyles = makeStyles(() => ({
  tableContainer: {
    borderRadius: '8px',
    border: '1px solid #E6E6E6',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#FFFFFF',
    maxHeight: '60vh',
    overflowY: 'auto',
    '& .MuiTable-root': {
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
  },
  tableHead: {
    backgroundColor: '#F8F9FA',
    '& .MuiTableCell-head': {
      backgroundColor: '#F8F9FA',
      borderBottom: '2px solid #E6E6E6',
      fontWeight: '600',
      fontSize: '14px',
      color: '#333333',
      padding: '16px 12px',
      fontFamily: '"General Sans", sans-serif',
      '&:first-of-type': {
        borderTopLeftRadius: '8px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '8px',
      },
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#F8F9FA',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#FAFBFC',
    },
    '&:nth-of-type(even):hover': {
      backgroundColor: '#F1F3F4',
    },
  },
  tableCell: {
    borderBottom: '1px solid #E6E6E6',
    padding: '16px 12px',
    fontSize: '14px',
    color: '#333333',
    fontFamily: '"General Sans", sans-serif',
    '&:first-of-type': {
      fontWeight: '500',
    },
  },
  startupNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  startupLogo: {
    width: '40px',
    height: '40px',
    backgroundColor: '#604797',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& .MuiTypography-root': {
      fontSize: '14px',
      fontWeight: '600',
      color: '#FFFFFF',
      fontFamily: '"General Sans", sans-serif',
    },
  },
  startupInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  startupName: {
    fontWeight: '600',
    fontSize: '14px',
    color: '#333333',
    fontFamily: '"General Sans", sans-serif',
  },
  startupDescription: {
    fontSize: '12px',
    color: '#666666',
    fontFamily: '"General Sans", sans-serif',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
  },
  industryCell: {
    fontSize: '14px',
    color: '#333333',
    fontFamily: '"General Sans", sans-serif',
  },
  subCategoryCell: {
    fontSize: '14px',
    color: '#666666',
    fontFamily: '"General Sans", sans-serif',
  },
  actionButton: {
    minWidth: '140px',
    height: '32px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'none',
    borderRadius: '6px',
    fontFamily: '"General Sans", sans-serif',
    padding: '6px 12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&.view-button': {
      backgroundColor: '#604797',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#4A3A7A',
      },
    },
    '&.begin-button': {
      backgroundColor: '#604797',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#4A3A7A',
      },
    },
  },
  pagination: {
    borderTop: '1px solid #E6E6E6',
    backgroundColor: '#F8F9FA',
    '& .MuiTablePagination-toolbar': {
      minHeight: '52px',
      paddingLeft: '16px',
      paddingRight: '16px',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      gap: '12px',
      justifyContent: 'flex-end',
    },
    '& .MuiTablePagination-spacer': {
      display: 'none',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      margin: 0,
      whiteSpace: 'nowrap',
    },
    '& .MuiTablePagination-selectLabel': {
      fontSize: '14px',
      color: '#333333',
      fontFamily: '"General Sans", sans-serif',
    },
    '& .MuiTablePagination-displayedRows': {
      fontSize: '14px',
      color: '#333333',
      fontFamily: '"General Sans", sans-serif',
    },
    '& .MuiTablePagination-actions': {
      marginLeft: '8px',
      flex: '0 0 auto',
      whiteSpace: 'nowrap',
    },
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#666666',
    fontFamily: '"General Sans", sans-serif',
  },
  // Mobile: cards instead of a raw table, matching the Dashboard's own
  // assigned-startup card style so "View Evaluation" reads the same everywhere.
  mobileCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  mobileCard: {
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
    padding: '14px',
  },
  mobileCardTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
  },
  mobileCardActionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: '0.5px solid #D1C8E3',
  },
  mobileViewEvaluationButton: {
    padding: '0 !important',
    minWidth: 'auto !important',
    color: '#604797 !important',
    fontWeight: '500 !important' as any,
    fontSize: '13px !important',
    fontFamily: '"General Sans", sans-serif !important',
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  sortLabel: {
    '& .MuiTableSortLabel-icon': {
      color: '#604797 !important',
    },
    '&.Mui-active': {
      color: '#604797 !important',
    },
  },
}));

interface StartupData {
  Id: string;
  Application__c: string;
  CreatedDate: string;
  Application__r: {
    Name_of_Product_or_Startup_Venture__c: string;
    Name: string;
    Industry__c: string;
    Indicate_industry_you_are_serving__c: string;
    Product_Service_Outline_in_one_statement__c?: string;
  };
  Evaluation_Scores__r?: {
    records: Array<{
      Evaluated__c: boolean;
    }>;
  };
  Start_Date_Time__c?: string;
  Meeting_Link__c?: string;
}

interface StartupTableProps {
  data: StartupData[];
  onViewStartup?: (startup: StartupData) => void;
  showPagination?: boolean;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
}

type Order = 'asc' | 'desc';
type OrderBy = 'startupName' | 'industry' | 'createdDate';

const StartupTable: React.FC<StartupTableProps> = ({
  data,
  onViewStartup,
  showPagination = true,
  rowsPerPageOptions = [5, 10, 25],
  defaultRowsPerPage = 10,
}) => {
  const classes = useStyles();
  const navigate = useHistory();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('startupName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewStartup = async (startup: StartupData) => {
    if (onViewStartup) {
      onViewStartup(startup);
    } else {
      await storageService.setItem('PanelId', startup.Id);
      navigate.push(`/startup-profile/${startup.Application__c}`, {
        startupHeader: startup.Application__r,
        isEvaluated: startup.Evaluation_Scores__r?.records?.[0]?.Evaluated__c,
        tabState: 3,
      });
    }
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (orderBy) {
        case 'startupName':
          aValue = a.Application__r?.Name_of_Product_or_Startup_Venture__c || a.Application__r?.Name || '';
          bValue = b.Application__r?.Name_of_Product_or_Startup_Venture__c || b.Application__r?.Name || '';
          break;
        case 'industry':
          aValue = a.Application__r?.Industry__c || '';
          bValue = b.Application__r?.Industry__c || '';
          break;
        case 'createdDate':
          aValue = a.CreatedDate || '';
          bValue = b.CreatedDate || '';
          break;
        default:
          aValue = '';
          bValue = '';
      }

      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [data, order, orderBy]);

  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, showPagination]);

  const isEvaluated = (startup: StartupData) => {
    return startup.Evaluation_Scores__r?.records?.[0]?.Evaluated__c === true;
  };

  if (data.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body1">No startups assigned yet</Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box className={classes.mobileCardList}>
        {paginatedData.map((startup, index) => (
          <Box key={startup.Id || index} className={classes.mobileCard}>
            <Box className={classes.mobileCardTopRow}>
              <Box className={classes.startupLogo}>
                <Typography>
                  {getFirstLetters(
                    startup.Application__r?.Name_of_Product_or_Startup_Venture__c ||
                    startup.Application__r?.Name ||
                    'ST'
                  )}
                </Typography>
              </Box>
              <Box className={classes.startupInfo}>
                <Typography className={classes.startupName}>
                  {startup.Application__r?.Name_of_Product_or_Startup_Venture__c ||
                    startup.Application__r?.Name ||
                    'Startup Name'}
                </Typography>
                <Typography className={classes.industryCell} sx={{ fontSize: '12px !important', color: '#666666 !important' }}>
                  {startup.Application__r?.Industry__c || 'N/A'}
                  {startup.CreatedDate ? ` · ${new Date(startup.CreatedDate).toLocaleDateString()}` : ''}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.mobileCardActionRow}>
              <Button
                variant="text"
                size="small"
                className={classes.mobileViewEvaluationButton}
                endIcon={<KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
                onClick={() => handleViewStartup(startup)}
              >
                {isEvaluated(startup) ? 'View Evaluation' : 'Begin Evaluation'}
              </Button>
            </Box>
          </Box>
        ))}
        {showPagination && (
          <TablePagination
            component="div"
            count={sortedData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            className={classes.pagination}
          />
        )}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table stickyHeader>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'startupName'}
                direction={orderBy === 'startupName' ? order : 'asc'}
                onClick={() => handleRequestSort('startupName')}
                className={classes.sortLabel}
              >
                Startup Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'industry'}
                direction={orderBy === 'industry' ? order : 'asc'}
                onClick={() => handleRequestSort('industry')}
                className={classes.sortLabel}
              >
                Industry
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'createdDate'}
                direction={orderBy === 'createdDate' ? order : 'asc'}
                onClick={() => handleRequestSort('createdDate')}
                className={classes.sortLabel}
              >
                Created Date
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((startup, index) => (
            <TableRow key={startup.Id || index} className={classes.tableRow}>
              <TableCell className={classes.tableCell}>
                <Box className={classes.startupNameCell}>
                  <Box className={classes.startupLogo}>
                    <Typography>
                      {getFirstLetters(
                        startup.Application__r?.Name_of_Product_or_Startup_Venture__c ||
                        startup.Application__r?.Name ||
                        'ST'
                      )}
                    </Typography>
                  </Box>
                  <Box className={classes.startupInfo}>
                    <Typography className={classes.startupName}>
                      {startup.Application__r?.Name_of_Product_or_Startup_Venture__c ||
                        startup.Application__r?.Name ||
                        'Startup Name'}
                    </Typography>
                    {startup.Application__r?.Product_Service_Outline_in_one_statement__c && (
                      <Typography className={classes.startupDescription}>
                        {startup.Application__r.Product_Service_Outline_in_one_statement__c}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.industryCell}>
                  {startup.Application__r?.Industry__c || 'N/A'}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.subCategoryCell}>
                  {startup.CreatedDate ? new Date(startup.CreatedDate).toLocaleDateString() : 'N/A'}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <Button
                  variant="contained"
                  className={`${classes.actionButton} ${
                    isEvaluated(startup) ? 'view-button' : 'begin-button'
                  }`}
                  startIcon={
                    isEvaluated(startup) ? (
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  onClick={() => handleViewStartup(startup)}
                >
                  {isEvaluated(startup) ? 'View' : 'Begin Evaluation'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {showPagination && (
        <TablePagination
          component="div"
          count={sortedData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          className={classes.pagination}
        />
      )}
    </TableContainer>
  );
};

export default StartupTable;
